import { Tag } from 'antd';
import type { ReactNode } from 'react';

import {
  CustomerGroup,
  CustomerIndustry,
  CustomerLevel,
  CustomerStatus,
} from '../../api/customers';

/** Một chọn lựa trong ô chọn. */
export interface SelectOption {
  readonly value: number;
  readonly label: string;
}

/**
 * Tên hiển thị của bốn cấp trong cây khách hàng.
 *
 * Dùng đúng từ mà người làm việc thật dùng: *pháp nhân* chứ không phải *cấp 2*. Người nhập dữ liệu
 * không nhớ số cấp, nhưng họ biết rõ hóa đơn xuất cho pháp nhân nào và hàng giao tới nhà máy nào.
 */
export const levelOptions: readonly SelectOption[] = [
  { value: CustomerLevel.Group, label: 'Tập đoàn' },
  { value: CustomerLevel.LegalEntity, label: 'Pháp nhân' },
  { value: CustomerLevel.Site, label: 'Nhà máy / Địa điểm' },
  { value: CustomerLevel.Line, label: 'Line / Khu vực' },
];

/** Tên hiển thị của năm trạng thái quan hệ. */
export const statusOptions: readonly SelectOption[] = [
  { value: CustomerStatus.Prospect, label: 'Tiềm năng' },
  { value: CustomerStatus.Active, label: 'Đang hoạt động' },
  { value: CustomerStatus.Suspended, label: 'Tạm ngừng' },
  { value: CustomerStatus.Terminated, label: 'Đã dừng hợp tác' },
  { value: CustomerStatus.Blacklisted, label: 'Danh sách đen' },
];

/** Tên hiển thị của tám ngành sản xuất. */
export const industryOptions: readonly SelectOption[] = [
  { value: CustomerIndustry.Electronics, label: 'Điện tử' },
  { value: CustomerIndustry.PrecisionMechanics, label: 'Cơ khí chính xác' },
  { value: CustomerIndustry.Automotive, label: 'Ô tô và xe máy' },
  {
    value: CustomerIndustry.PharmaceuticalAndMedicalDevice,
    label: 'Dược phẩm và thiết bị y tế',
  },
  { value: CustomerIndustry.FoodAndBeverage, label: 'Thực phẩm và đồ uống' },
  { value: CustomerIndustry.Packaging, label: 'Bao bì' },
  { value: CustomerIndustry.Textile, label: 'Dệt may' },
  { value: CustomerIndustry.Other, label: 'Ngành khác' },
];

/** Tên hiển thị của bốn nhóm khách hàng. */
export const groupOptions: readonly SelectOption[] = [
  { value: CustomerGroup.Multinational, label: 'Tập đoàn đa quốc gia' },
  { value: CustomerGroup.ForeignInvested, label: 'Doanh nghiệp FDI' },
  { value: CustomerGroup.Domestic, label: 'Doanh nghiệp trong nước' },
  { value: CustomerGroup.IntegratorOrReseller, label: 'Nhà tích hợp / đại lý' },
];

/** Tìm tên hiển thị theo giá trị, trả về dấu gạch khi không khai. */
function findLabel(options: readonly SelectOption[], value: number | null): string {
  if (value === null) {
    return '—';
  }

  return (
    options.find((option) => option.value === value)?.label
    ?? `Giá trị lạ (${String(value)})`
  );
}

/** Tên hiển thị của một cấp. */
export function levelLabel(level: number): string {
  return findLabel(levelOptions, level);
}

/**
 * Nhãn màu cho trạng thái quan hệ.
 *
 * Ba trạng thái chặn giao dịch dùng màu cảnh báo hoặc màu đỏ, vì đó không phải thông tin trang trí:
 * người nhìn vào danh sách cần thấy ngay khách nào không lập được báo giá, trước khi họ bắt đầu gõ.
 */
export function statusTag(status: number): ReactNode {
  switch (status) {
    case CustomerStatus.Prospect:
      return <Tag color="blue">Tiềm năng</Tag>;

    case CustomerStatus.Active:
      return <Tag color="green">Đang hoạt động</Tag>;

    case CustomerStatus.Suspended:
      return <Tag color="orange">Tạm ngừng</Tag>;

    case CustomerStatus.Terminated:
      return <Tag color="red">Đã dừng hợp tác</Tag>;

    case CustomerStatus.Blacklisted:
      return <Tag color="magenta">Danh sách đen</Tag>;

    default:
      return <Tag>Trạng thái lạ ({String(status)})</Tag>;
  }
}

/**
 * Nhãn màu cho cấp trong cây.
 *
 * Chỉ cấp pháp nhân được tô màu. Đó là cấp duy nhất có mã số thuế và là cấp xuất hóa đơn, nên khi
 * duyệt một danh sách dài thì mắt cần tìm ra nó trước tiên.
 */
export function levelTag(level: number): ReactNode {
  return level === CustomerLevel.LegalEntity ? (
    <Tag color="geekblue">Pháp nhân</Tag>
  ) : (
    <Tag>{levelLabel(level)}</Tag>
  );
}

/**
 * Chuỗi thụt lề theo cấp sâu của nút trong cây.
 *
 * Danh sách được máy chủ sắp theo đường dẫn nên mọi nút con nằm ngay dưới nút cha của nó. Thụt lề là
 * thứ duy nhất còn thiếu để một bảng phẳng đọc được như một cây.
 */
export function indentFor(depth: number): string {
  return '    '.repeat(depth);
}
